import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { GroupsSharedModule } from '@app/groups/groups-shared.module';
import { InsightsModule } from '@app/insights/insights.module';
import { OrgSettingsService } from '@app/orgs/services/org-settings.service';
import { AutofocusDirective } from '@app/shared/directives/autofocus.directive';

import { FtpSharedModule } from './../ftp/ftp-shared.module';
import { ConfirmMovePlanModalComponent } from './components/confirm-move-plan-modal/confirm-move-plan-modal.component';

// components
import {
  SettingTileComponent,
  SettingTileHeaderComponent,
  SettingTileToggleTextComponent,
} from '@degreed/lxp/org/settings';
import {
  SettingTileSaveFooterComponent,
  SettingTileValidationMessageComponent,
} from '@degreed/lxp/org/ui';
import { OrgProvidersService } from '@degreed/lxp/services/org';
import { AddOrgCollaboratorsModalComponent } from './components/add-collaborators-modal/add-collaborators-modal.component';
import { EditMemberModalComponent } from './components/edit-member-modal/edit-member-modal.component';
import { EditVisibilityModalComponent } from './components/edit-visibility-modal/edit-visibility-modal.component';
import { EditVisibilityModalService } from './components/edit-visibility-modal/edit-visibility-modal.service';
import { OrgFaqEmptyComponent } from './components/org-faq-empty/org-faq-empty.component';
import { OrgFaqEntryComponent } from './components/org-faq-entry/org-faq-entry.component';
import { OrgFaqViewComponent } from './components/org-faq-view/org-faq-view.component';
import { OrgFaqComponent } from './components/org-faq/org-faq.component';
import { OrgGroupsHeaderComponent } from './components/org-groups-header/org-groups-header.component';
import { OrgGroupsComponent } from './components/org-groups/org-groups.component';
import { MemberCountsComponent } from './components/org-members/member-counts/member-counts.component';
import { OrgMembersHeaderComponent } from './components/org-members/org-members-header/org-members-header.component';
import { OrgMembersComponent } from './components/org-members/org-members.component';
import { OrgPlansComponent } from './components/org-plans/org-plans.component';
import { OrgReportingComponent } from './components/org-reporting/org-reporting.component';
import { OrgSecurityComponent } from './components/org-security/org-security.component';
import { SettingTileToggleJsonComponent } from './components/setting-tile-toggle-json/setting-tile-toggle-json.component';

// providers
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ComboboxComponent } from '@app/shared/components/combobox/combobox.component';
import { DataBulkActionButtonComponent } from '@app/shared/components/data-table/data-bulk-actions/data-bulk-action-button/data-bulk-action-button.component';
import { DataBulkActionsComponent } from '@app/shared/components/data-table/data-bulk-actions/data-bulk-actions.component';
import { DataFiltersComponent } from '@app/shared/components/data-table/data-filters/data-filters.component';
import { DataSearchComponent } from '@app/shared/components/data-table/data-search/data-search.component';
import { DataTableComponent } from '@app/shared/components/data-table/data-table.component';
import { FinraTextareaLimitComponent } from '@app/shared/components/finra-textarea-limit/finra-textarea-limit.component';
import { FooterGlassesComponent } from '@app/shared/components/footer-glasses/footer-glasses.component';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { NoSearchResultsComponent } from '@app/shared/components/no-search-results/no-search-results.component';
import { PopoverTriggerDirective } from '@app/shared/components/popover/popover-trigger.directive';
import { PopoverComponent } from '@app/shared/components/popover/popover.component';
import { ProfilePicComponent } from '@app/shared/components/profile-pic/profile-pic.component';
import { StickyFilterBarComponent } from '@app/shared/components/sticky-filter-bar/sticky-filter-bar.component';
import { ToggleElementComponent } from '@app/shared/components/toggle-element/toggle-element.component';
import { VisibilityComponent } from '@app/shared/components/visibility/visibility.component';
import { PredicateListComponent } from '@degreed/lxp/org/automations';

import { RecommendationsStatusComponent } from '@app/recommendations/components/recommendations-status/recommendations-status.component';
import { UserModule } from '@app/user/user.module';
import { IconComponent } from '@degreed/apollo';
import { EllipsisPipe } from '@degreed/core/pipes';
import {
  BlobifyUrlPipe,
  ItemContainerRoleDirective,
  ItemRoleDirective,
  ScrollService,
  StickyDirective,
} from '@degreed/lxp/services';
import { SkeletonComponent } from '@degreed/lxp/ui';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { AuthorsListComponent } from './components/authors-list/authors-list.component';
import { OrgBulkUploadModalComponent } from './components/org-bulk-upload-modal/org-bulk-upload-modal.component';
import { OrgBulkUploadSuccessModalComponent } from './components/org-bulk-upload-success-modal/org-bulk-upload-success-modal.component';
import { BadgeListComponent } from './components/org-manage-user-permissions/components/badge-list/badge-list.component';
import { OrgPlansFacade } from './components/org-plans/rsm/org-plans.facade';
import { PlanLinkComponent } from './components/plan-link/plan-link.component';
import { OrgInfoService } from './services/org-info.service';
import { OrgPlansModalService } from './services/org-plans-modal.service';
import { OrgService } from './services/org.service';

/**
 * Shared module for Manage Org Component and Services
 *
 * NOTE: this module is eager loaded through the root AppModule to ensure that these providers
 * will exist on the root injector for lazily downgraded components.
 */
@NgModule({
  declarations: [
    EditVisibilityModalComponent,

    // These components are also used in GAT pages (`/channel`)
    OrgReportingComponent,
    OrgSecurityComponent,
    // These components are also used in the FAQ page (`/faq/:orgId`)
    OrgFaqEmptyComponent,
    OrgFaqEntryComponent,
    OrgFaqViewComponent,
    OrgFaqComponent,
    OrgMembersComponent,
    MemberCountsComponent,
    OrgMembersHeaderComponent,
    OrgGroupsComponent,
    OrgGroupsHeaderComponent,
    ConfirmMovePlanModalComponent,
    OrgPlansComponent,
    AuthorsListComponent,
    PlanLinkComponent,
    EditMemberModalComponent,
    AddOrgCollaboratorsModalComponent,
  ],
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    InsightsModule,
    FtpSharedModule,
    GroupsSharedModule,
    UserModule,
    RouterModule,
    CommonModule,
    NgbTooltipModule,
    //standalone components
    AutofocusDirective,
    SettingTileComponent,
    SettingTileHeaderComponent,
    SettingTileSaveFooterComponent,
    SettingTileValidationMessageComponent,
    SettingTileToggleJsonComponent,
    SettingTileToggleTextComponent,
    SkeletonComponent, // OrgFaqComponent, OrgSecurityComponent
    BadgeListComponent, // OrgMembersComponent
    PredicateListComponent, // OrgGroupsComponent
    OrgBulkUploadModalComponent,
    OrgBulkUploadSuccessModalComponent, // AddEditTagModalComponent
    EllipsisPipe,
    BlobifyUrlPipe,
    ItemContainerRoleDirective,
    ItemRoleDirective,
    StickyDirective,
    PopoverTriggerDirective,
    VisibilityComponent,
    ProfilePicComponent,
    ModalComponent,
    ComboboxComponent,
    DataBulkActionButtonComponent,
    DataBulkActionsComponent,
    DataFiltersComponent,
    DataSearchComponent,
    DataTableComponent,
    FinraTextareaLimitComponent,
    FooterGlassesComponent,
    PopoverComponent,
    StickyFilterBarComponent,
    NoSearchResultsComponent,
    ToggleElementComponent,
    RecommendationsStatusComponent,
    IconComponent,
  ],
  providers: [
    EditVisibilityModalService,
    OrgProvidersService,
    OrgSettingsService,
    OrgService,
    OrgInfoService,
    OrgPlansFacade,
    OrgPlansModalService,
    ScrollService,
  ],
  exports: [
    EditVisibilityModalComponent,
    // Components
    OrgReportingComponent,
    OrgFaqViewComponent,
    OrgFaqComponent,
    OrgSecurityComponent,
    OrgMembersComponent,
    OrgGroupsComponent,
    SettingTileComponent,
    OrgPlansComponent,
    OrgBulkUploadModalComponent,
    OrgBulkUploadSuccessModalComponent,
    AuthorsListComponent,
    PlanLinkComponent,
    EditMemberModalComponent,
    AddOrgCollaboratorsModalComponent,
    SettingTileToggleTextComponent,
    SettingTileToggleJsonComponent,
  ],
})
export class OrgsSharedModule {}
